import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { whmcsApi, whmcsBulkApi, customersApi } from '../services/api';
import { Customer } from '../types';
import toast from 'react-hot-toast';
import './WHMCS.css';

type Tab = 'products' | 'domains' | 'clients';
type MapFilter = 'all' | 'mapped' | 'unmapped';

interface WBilling {
  unit_price: string | null;
  billing_cycle: string | null;
  monthly_total: number;
  is_override: boolean;
}
interface WItem {
  id: number;
  whmcs_id: number;
  product_name?: string;
  domain_name?: string;
  display_name?: string;
  product_group?: string;
  domain?: string;
  email?: string;
  status?: string;
  billing_cycle?: string;
  recurring_amount?: number;
  expiry_date?: string;
  customer_id: number | null;
  customer_name: string | null;
  gone?: boolean;            // mapped item whose WHMCS status is cancelled/terminated -> red, nothing billed
  billing?: WBilling | null;
}

const rand = (n: number) =>
  `R ${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const itemName = (i: WItem) => i.product_name || i.domain_name || i.display_name || `#${i.whmcs_id}`;

function WHMCS() {
  const [status, setStatus] = useState<{ connected: boolean; total_clients?: number; error?: string } | null>(null);
  const [tab, setTab] = useState<Tab>('products');
  const [products, setProducts] = useState<WItem[]>([]);
  const [domains, setDomains] = useState<WItem[]>([]);
  const [clients, setClients] = useState<WItem[]>([]);
  const [counts, setCounts] = useState<any>({});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [search, setSearch] = useState('');
  const [mapFilter, setMapFilter] = useState<MapFilter>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Map / edit modal
  const [mapItem, setMapItem] = useState<WItem | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');

  const list = tab === 'products' ? products : tab === 'domains' ? domains : clients;
  const hasPricing = tab !== 'clients';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [st, cu, ss] = await Promise.all([
        whmcsApi.getStatus().then(r => r.data).catch(() => ({ connected: false, error: 'Not configured' })),
        customersApi.list(),
        whmcsApi.getSyncStatus().then(r => r.data).catch(() => ({ counts: {} })),
      ]);
      setStatus(st);
      setCustomers(cu);
      setCounts(ss.counts || {});
      const [p, d, c] = await Promise.all([
        whmcsApi.getProducts().then(r => r.data).catch(() => []),
        whmcsApi.getDomains().then(r => r.data).catch(() => []),
        whmcsApi.getClients().then(r => r.data).catch(() => []),
      ]);
      setProducts(p); setDomains(d); setClients(c);
    } catch {
      toast.error('Failed to load WHMCS data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const reload = async () => {
    const [p, d, c, ss] = await Promise.all([
      whmcsApi.getProducts().then(r => r.data).catch(() => []),
      whmcsApi.getDomains().then(r => r.data).catch(() => []),
      whmcsApi.getClients().then(r => r.data).catch(() => []),
      whmcsApi.getSyncStatus().then(r => r.data).catch(() => ({ counts: {} })),
    ]);
    setProducts(p); setDomains(d); setClients(c); setCounts(ss.counts || {});
  };

  const runSync = async () => {
    setBusy('sync');
    try {
      await Promise.all([whmcsApi.syncClients(), whmcsApi.syncDomains(), whmcsApi.syncProducts()]);
      toast.success('Synced from WHMCS');
      await reload();
    } catch {
      toast.error('Sync failed');
    } finally { setBusy(''); }
  };

  const runAutoMap = async () => {
    setBusy('automap');
    try {
      const r = await whmcsApi.autoMap();
      toast.success(`Auto-mapped: ${r.data.matched} matched, ${r.data.created} new`);
      await reload();
    } catch {
      toast.error('Auto-map failed');
    } finally { setBusy(''); }
  };

  const openMap = (item: WItem) => {
    setMapItem(item);
    setSelectedCustomer(item.customer_id);
    setCustomerSearch('');
    setPrice(item.billing?.unit_price ?? (item.recurring_amount != null ? String(item.recurring_amount) : ''));
  };

  const doMap = async () => {
    if (!mapItem || !selectedCustomer) { toast.error('Select a customer'); return; }
    setSaving(true);
    try {
      if (tab === 'products') await whmcsApi.mapProduct(mapItem.id, selectedCustomer, price || undefined);
      else if (tab === 'domains') await whmcsApi.mapDomain(mapItem.id, selectedCustomer, price || undefined);
      else await whmcsApi.mapClient(mapItem.id, selectedCustomer);
      toast.success('Mapped');
      setMapItem(null);
      await reload();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to map');
    } finally { setSaving(false); }
  };

  const doUnmap = async (item: WItem) => {
    try {
      if (tab === 'products') await whmcsApi.unmapProduct(item.id);
      else if (tab === 'domains') await whmcsApi.unmapDomain(item.id);
      else await whmcsApi.unmapClient(item.id);
      toast.success('Unmapped');
      await reload();
    } catch { toast.error('Failed to unmap'); }
  };

  const doBulk = async () => {
    if (selected.size === 0 || !selectedCustomer) { toast.error('Select items and a customer'); return; }
    setSaving(true);
    try {
      const ids = Array.from(selected);
      if (tab === 'products') await whmcsBulkApi.bulkMapProducts(ids, selectedCustomer, bulkPrice || undefined);
      else if (tab === 'domains') await whmcsBulkApi.bulkMapDomains(ids, selectedCustomer, bulkPrice || undefined);
      else await whmcsBulkApi.bulkMapClients(ids, selectedCustomer);
      toast.success(`Mapped ${ids.length} items`);
      setBulkOpen(false); setSelected(new Set()); setSelectedCustomer(null); setBulkPrice('');
      await reload();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Bulk map failed');
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter(i => {
      const mapOk = mapFilter === 'all' || (mapFilter === 'mapped' && i.customer_id) || (mapFilter === 'unmapped' && !i.customer_id);
      const searchOk = !q || itemName(i).toLowerCase().includes(q) || (i.customer_name || '').toLowerCase().includes(q) || (i.email || '').toLowerCase().includes(q);
      return mapOk && searchOk;
    });
  }, [list, search, mapFilter]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.toLowerCase();
    return customers.filter(c => !q || c.xero_name.toLowerCase().includes(q)).slice(0, 100);
  }, [customers, customerSearch]);

  const mappedCount = list.filter(i => i.customer_id).length;
  const unmappedInView = filtered.filter(i => !i.customer_id);
  const allSelected = unmappedInView.length > 0 && unmappedInView.every(i => selected.has(i.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(unmappedInView.map(i => i.id)));
  const toggleOne = (id: number) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); setSelected(n); };

  const modalTotal = useMemo(() => (parseFloat(price) || 0), [price]);

  if (loading) return <div className="whmcs-page"><div className="whmcs-loading">Loading WHMCS…</div></div>;

  return (
    <div className="whmcs-page">
      <div className="whmcs-header">
        <div>
          <h1>WHMCS Hosting &amp; Domains</h1>
          <p className="whmcs-subtitle">Map hosting products &amp; domains to customers for billing</p>
        </div>
        <div className="whmcs-header-actions">
          <button className="whmcs-btn ghost" onClick={runSync} disabled={busy === 'sync'}>
            {busy === 'sync' ? '↻ Syncing…' : '↻ Sync from WHMCS'}
          </button>
          <button className="whmcs-btn primary" onClick={runAutoMap} disabled={busy === 'automap'}>
            {busy === 'automap' ? '⟳ Mapping…' : '🔗 Auto-Map by Company'}
          </button>
        </div>
      </div>

      <div className={`whmcs-status ${status?.connected ? 'connected' : 'disconnected'}`}>
        <span className="whmcs-status-dot" />
        <strong>{status?.connected ? 'Connected' : 'Not connected'}</strong>
        {status?.connected && status.total_clients != null && <span className="whmcs-status-sub">{status.total_clients} WHMCS clients</span>}
        {status?.error && <span className="whmcs-status-err">{status.error}</span>}
      </div>

      <div className="whmcs-summary">
        <div className="whmcs-card"><div className="whmcs-card-val">{counts.products ?? 0}</div><div className="whmcs-card-lbl">Products</div></div>
        <div className="whmcs-card"><div className="whmcs-card-val">{counts.domains ?? 0}</div><div className="whmcs-card-lbl">Domains</div></div>
        <div className="whmcs-card"><div className="whmcs-card-val">{counts.clients ?? 0}</div><div className="whmcs-card-lbl">Clients</div></div>
        <div className="whmcs-card ok"><div className="whmcs-card-val">{(counts.products_mapped ?? 0) + (counts.domains_mapped ?? 0)}</div><div className="whmcs-card-lbl">Mapped (billable)</div></div>
        <div className="whmcs-card total"><div className="whmcs-card-val">{rand(counts.monthly_total ?? 0)}</div><div className="whmcs-card-lbl">Billable / month</div></div>
      </div>

      <div className="whmcs-tabs">
        {(['products', 'domains', 'clients'] as Tab[]).map(t => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => { setTab(t); setSelected(new Set()); }}>
            {t === 'products' ? 'Products' : t === 'domains' ? 'Domains' : 'Clients'}
            <span className="whmcs-tab-count">{t === 'products' ? products.length : t === 'domains' ? domains.length : clients.length}</span>
          </button>
        ))}
      </div>

      <div className="whmcs-toolbar">
        <div className="whmcs-chip-group">
          {(['all', 'mapped', 'unmapped'] as MapFilter[]).map(f => (
            <button key={f} className={mapFilter === f ? 'active' : ''} onClick={() => setMapFilter(f)}>
              {f === 'all' ? `All (${list.length})` : f === 'mapped' ? `Mapped (${mappedCount})` : `Unmapped (${list.length - mappedCount})`}
            </button>
          ))}
        </div>
        <input className="whmcs-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        {selected.size > 0 && (
          <button className="whmcs-btn primary" onClick={() => { setSelectedCustomer(null); setBulkOpen(true); }}>
            Map {selected.size} selected
          </button>
        )}
      </div>

      <div className="whmcs-table-wrap">
        <table className="whmcs-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all unmapped in view" /></th>
              <th>{tab === 'products' ? 'Product' : tab === 'domains' ? 'Domain' : 'Client'}</th>
              <th>{tab === 'clients' ? 'Email' : 'Detail'}</th>
              <th>Status</th>
              <th>Mapped Customer</th>
              {hasPricing && <th className="whmcs-num">Unit Price</th>}
              {hasPricing && <th className="whmcs-num">Monthly</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={hasPricing ? 8 : 6} className="whmcs-empty">No {tab} match the filters. {list.length === 0 ? 'Try “Sync from WHMCS”.' : ''}</td></tr>
            ) : filtered.map(i => (
              <tr key={i.id} className={`${i.customer_id ? 'mapped' : ''}${i.gone ? ' gone' : ''}`}
                  title={i.gone ? `WHMCS says ${i.status} - nothing is billed, its line is off in Invoice Prep` : undefined}>
                <td>{!i.customer_id && <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggleOne(i.id)} />}</td>
                <td className="whmcs-name">{itemName(i)}{i.gone && <span className="whmcs-tag danger" title={`WHMCS status ${i.status}`}>{(i.status || 'gone').toLowerCase()} · not billed</span>}</td>
                <td className="whmcs-muted">{tab === 'clients' ? i.email : tab === 'products' ? (i.product_group || i.domain || '—') : (i.expiry_date || '—')}</td>
                <td><span className={`whmcs-pill ${(i.status || '').toLowerCase()}`}>{i.status || '—'}</span></td>
                <td>{i.customer_name ? <span className="whmcs-match ok">{i.customer_name}</span> : <span className="whmcs-match no">Not mapped</span>}</td>
                {hasPricing && <td className="whmcs-num">{i.billing?.unit_price ? <span className={i.customer_id ? '' : 'whmcs-preview'}>{rand(parseFloat(i.billing.unit_price))}<span className="whmcs-per">/{(i.billing.billing_cycle || '').toLowerCase().slice(0, 3) || 'mo'}</span></span> : '–'}</td>}
                {hasPricing && <td className="whmcs-num">{i.billing ? <strong className={i.customer_id ? 'whmcs-money' : 'whmcs-preview'}>{rand(i.billing.monthly_total)}</strong> : '–'}</td>}
                <td>
                  {i.customer_id ? (
                    <div className="whmcs-row-actions">
                      <button className="whmcs-btn xs" onClick={() => openMap(i)}>Edit</button>
                      <button className="whmcs-btn xs danger" onClick={() => doUnmap(i)}>Unmap</button>
                    </div>
                  ) : (
                    <button className="whmcs-btn xs primary" onClick={() => openMap(i)}>Map</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Map / Edit modal */}
      {mapItem && (
        <div className="whmcs-modal-overlay" onClick={() => setMapItem(null)}>
          <div className="whmcs-modal" onClick={e => e.stopPropagation()}>
            <div className="whmcs-modal-head">
              <h3>{mapItem.customer_id ? 'Edit mapping' : 'Map to customer'}</h3>
              <button className="whmcs-x" onClick={() => setMapItem(null)}>✕</button>
            </div>
            <div className="whmcs-modal-body">
              <div className="whmcs-item-info">
                <strong>{itemName(mapItem)}</strong>
                <span>{tab === 'products' ? (mapItem.product_group || '') : tab === 'domains' ? 'Domain' : mapItem.email}</span>
              </div>
              <label className="whmcs-field"><span>Search customer</span>
                <input autoFocus placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </label>
              <div className="whmcs-customer-list">
                {filteredCustomers.map(c => (
                  <div key={c.id} className={`whmcs-customer ${selectedCustomer === c.id ? 'selected' : ''}`} onClick={() => setSelectedCustomer(c.id)}>{c.xero_name}</div>
                ))}
                {filteredCustomers.length === 0 && <div className="whmcs-customer empty">No customers found</div>}
              </div>
              {hasPricing && (
                <div className="whmcs-pricing">
                  <label className="whmcs-field price"><span>Unit price (R) {mapItem.recurring_amount != null && <em>— WHMCS: {rand(mapItem.recurring_amount)}</em>}</span>
                    <input type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
                  </label>
                  <div className="whmcs-total-row"><span>Recurring</span><strong>{rand(modalTotal)} <span className="whmcs-cyc">/ {mapItem.billing_cycle || (tab === 'domains' ? 'Annually' : 'Monthly')}</span></strong></div>
                </div>
              )}
            </div>
            <div className="whmcs-modal-foot">
              <button className="whmcs-btn ghost" onClick={() => setMapItem(null)}>Cancel</button>
              <button className="whmcs-btn primary" onClick={doMap} disabled={!selectedCustomer || saving}>{saving ? 'Saving…' : mapItem.customer_id ? 'Save' : 'Map'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk modal */}
      {bulkOpen && (
        <div className="whmcs-modal-overlay" onClick={() => setBulkOpen(false)}>
          <div className="whmcs-modal" onClick={e => e.stopPropagation()}>
            <div className="whmcs-modal-head"><h3>Map {selected.size} {tab}</h3><button className="whmcs-x" onClick={() => setBulkOpen(false)}>✕</button></div>
            <div className="whmcs-modal-body">
              <label className="whmcs-field"><span>Search customer</span>
                <input autoFocus placeholder="Type to search…" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
              </label>
              <div className="whmcs-customer-list">
                {filteredCustomers.map(c => (
                  <div key={c.id} className={`whmcs-customer ${selectedCustomer === c.id ? 'selected' : ''}`} onClick={() => setSelectedCustomer(c.id)}>{c.xero_name}</div>
                ))}
              </div>
              {hasPricing && (
                <label className="whmcs-field price"><span>Override unit price for all (optional — blank keeps each WHMCS amount)</span>
                  <input type="number" min="0" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="Keep WHMCS amounts" />
                </label>
              )}
            </div>
            <div className="whmcs-modal-foot">
              <button className="whmcs-btn ghost" onClick={() => setBulkOpen(false)}>Cancel</button>
              <button className="whmcs-btn primary" onClick={doBulk} disabled={!selectedCustomer || saving}>{saving ? 'Saving…' : `Map ${selected.size}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WHMCS;
